import React, { useEffect, useState } from "react";
import axios from "../../../utills/api";
import { Col, Card, CardBody, CardHeader } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";

const TeamMembers = () => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const [teamRes, housekeepingRes, foodRes, spaRes] = await Promise.all([
          axios.get("/team"),
          axios.get("/requests/guest"),
          axios.get("/food/order"),
          axios.get("/spa"),
        ]);

        const teamData = Array.isArray(teamRes.data) ? teamRes.data : [];
        const housekeepingData = Array.isArray(housekeepingRes.data)
          ? housekeepingRes.data
          : [];
        const foodData = Array.isArray(foodRes.data) ? foodRes.data : [];
        const spaData = Array.isArray(spaRes.data) ? spaRes.data : [];

        const teamStats = teamData.map((member) => {
          const memberName = member.name?.trim().toLowerCase();

          const housekeepingTasks = housekeepingData.filter(
            (req) =>
              req.assignedTo &&
              req.assignedTo.trim().toLowerCase() === memberName
          );

          const spaTasks = spaData.filter(
            (spa) =>
              spa.assignedTo &&
              spa.assignedTo.trim().toLowerCase() === memberName
          );

          const foodTasks = foodData.filter(
            (order) =>
              order.assignedTo &&
              order.assignedTo.trim().toLowerCase() === memberName
          );

          const allTasks = [...housekeepingTasks, ...spaTasks, ...foodTasks];

          const completedCount = allTasks.filter(
            (task) =>
              task.status?.toLowerCase().includes("complete") ||
              task.status === "Completed"
          ).length;

          const totalTasks = allTasks.length;
          const completionPercent =
            totalTasks > 0
              ? Math.round((completedCount / totalTasks) * 100)
              : 0;

          return {
            id: member._id,
            name: member.name,
            department: member.department,
            totalTasks,
            completedCount,
            completionPercent,
          };
        });

        const sortedMembers = teamStats.sort(
          (a, b) => b.totalTasks - a.totalTasks
        );
        setMembers(sortedMembers);
      } catch (err) {
        console.error("❌ Error fetching team data:", err);
        toast.error("Failed to fetch team data");
      }
    };

    fetchTeamData();
  }, []);

  // Radial chart with thicker outer bar
  const TeamMembersCharts = ({ seriesData }) => {
    const color = seriesData === 0 ? "#ff4d4d" : "#00c853";
    const series = [seriesData];
    const options = {
      chart: { type: "radialBar", sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          hollow: { size: "60%" }, // smaller hollow = thicker bar
          dataLabels: { show: false },
        },
      },
      colors: [color],
    };

    return (
      <div className="d-flex align-items-center gap-2">
        <ReactApexChart
          options={options}
          series={series}
          type="radialBar"
          height={25}
          width={25}
        />
        <span className="fs-13 text-muted">{seriesData}%</span>
      </div>
    );
  };

  // Table columns for TableContainer
  const columns = [
    {
      header: "Member",
      accessorKey: "name",
      cell: ({ row }) => <strong>{row.original.name}</strong>,
    },
    {
      header: "Department",
      accessorKey: "department",
      cell: ({ row }) => <span className="text-muted fs-12">{row.original.department}</span>,
    },
    {
      header: "Tasks",
      accessorKey: "totalTasks",
      cell: ({ row }) =>
        row.original.totalTasks > 0 ? (
          <span>
            {row.original.totalTasks}{" "}
            <span className="text-muted fs-12">
              ({row.original.completedCount} done)
            </span>
          </span>
        ) : (
          <span className="text-muted">No Tasks</span>
        ),
    },
    {
      header: "Status",
      accessorKey: "completionPercent",
      cell: ({ row }) => (
        <TeamMembersCharts seriesData={row.original.completionPercent} />
      ),
    },
  ];

  return (
    <React.Fragment>
      <Col xl={6} md={6}>
        <Card style={{height:"430px"}}>
          <CardHeader>
            <h4 className="card-title mb-0 flex-grow-1">Team Members</h4>
          </CardHeader>
          <CardBody>
            <TableContainer
              columns={columns}
              data={members}
              customPageSize={4}
              isPagination
              className="text-nowrap"
              theadClass="bg-light"
            />
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default TeamMembers;
